import { useEffect, useRef } from "react";

/* ════════════════════════════════════════════════════════════
   FeaturedVideo — self-contained, drop-in module
   Renders an autoplaying, muted, looping YouTube embed inside a
   styled section that matches the Coastal Fire / Imarika design
   system (Bebas Neue headings, orange/navy accents, reveal-on-
   scroll animation). No dependency on Home.jsx internals.

   USAGE:
     import FeaturedVideo from "../components/FeaturedVideo";
     ...
     <FeaturedVideo
       videoId="N2WSc5DoAJw"
       eyebrow="See Our Work"
       heading={<>WATCH OUR <span>STORY.</span></>}
     />
   ════════════════════════════════════════════════════════════ */

const STYLES = `
  .fv-section {
    background: #fff;
    padding: clamp(3rem, 6vw, 5rem) var(--cp, 1.5rem);
  }
  .fv-inner {
    max-width: var(--cw, 1280px);
    margin: 0 auto;
  }
  .fv-sec-label {
    display: flex;
    align-items: center;
    gap: .5rem;
    font-size: .62rem;
    font-weight: 800;
    letter-spacing: .22em;
    text-transform: uppercase;
    color: #ee4c05;
    margin-bottom: .875rem;
  }
  .fv-sec-label::before {
    content: '';
    width: 1.5rem;
    height: 2px;
    border-radius: 2px;
    background: #ee4c05;
    flex-shrink: 0;
  }
  .fv-heading {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(2.5rem, 7vw, 5.5rem);
    line-height: .9;
    letter-spacing: .02em;
    color: #043463;
    margin-bottom: clamp(2rem, 4vw, 3rem);
  }
  .fv-heading span { color: #ee4c05; }

  .fv-video-wrap {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    border-radius: 2px;
    overflow: hidden;
    background: #080808;
    border: 1px solid rgba(4, 52, 99, 0.08);
  }
  .fv-video-wrap iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }

  .fv-caption {
    margin-top: 1rem;
    font-size: .8rem;
    color: #6b7280;
    line-height: 1.75;
    max-width: 560px;
  }

  /* reveal-on-scroll */
  .fv-rv {
    opacity: 0;
    transform: translateY(28px);
    transition: opacity .72s ease, transform .72s ease;
  }
  .fv-rv.in { opacity: 1; transform: none; }
`;

/** Minimal, self-contained scroll-reveal wrapper (no external hooks needed). */
function FvReveal({ children, delay = 0 }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          el.classList.add("in");
          obs.disconnect();
        }
      },
      { threshold: 0.08, rootMargin: "0px 0px -40px 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={ref} className="fv-rv" style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

/**
 * FeaturedVideo
 *
 * @param {string}  videoId   - YouTube video ID (e.g. "N2WSc5DoAJw" from
 *                              https://youtu.be/N2WSc5DoAJw)
 * @param {string}  eyebrow   - small label above the heading
 * @param {node}    heading   - JSX for the heading (wrap the accent word in
 *                              <span> to get it in orange)
 * @param {string}  caption   - optional short paragraph under the video
 * @param {string}  title     - accessible iframe title
 * @param {boolean} autoplay  - default true (required to be muted per
 *                              browser policy)
 * @param {boolean} loop      - default true
 * @param {boolean} controls  - default true (recommended for accessibility —
 *                              lets users pause/unmute)
 * @param {boolean} sectionId - optional id="" for the <section>, so it can be
 *                              linked to from navigation (e.g. "#video")
 */
export default function FeaturedVideo({
  videoId,
  eyebrow = "See Our Work",
  heading = <>WATCH OUR <span>STORY.</span></>,
  caption,
  title = "Featured video",
  autoplay = true,
  loop = true,
  controls = true,
  sectionId,
}) {
  if (!videoId) return null;

  const params = new URLSearchParams({
    autoplay: autoplay ? "1" : "0",
    mute: autoplay ? "1" : "0", // browsers block autoplay with sound; muting is required
    controls: controls ? "1" : "0",
    rel: "0",
    modestbranding: "1",
  });
  if (loop) {
    params.set("loop", "1");
    params.set("playlist", videoId); // required for single-video looping on YouTube's embed API
  }

  return (
    <section
      id={sectionId}
      className="fv-section"
      style={sectionId ? { scrollMarginTop: "68px" } : undefined}
      aria-labelledby="fv-heading"
    >
      <style>{STYLES}</style>
      <div className="fv-inner">
        <FvReveal>
          <div className="fv-sec-label">{eyebrow}</div>
          <h2 id="fv-heading" className="fv-heading">{heading}</h2>
        </FvReveal>

        <FvReveal delay={100}>
          <div className="fv-video-wrap">
            <iframe
              src={`https://www.youtube.com/embed/${videoId}?${params.toString()}`}
              title={title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              loading="lazy"
            />
          </div>
          {caption && <p className="fv-caption">{caption}</p>}
        </FvReveal>
      </div>
    </section>
  );
}